import { pipe } from 'fp-ts/function';
import * as S from 'fp-ts/string';
import lodash from 'lodash';
import { v4 as uuid } from 'uuid';

import type {
  AppEmbedData,
  AudioData,
  BulletedListData,
  ButtonData,
  CodeBlockData,
  CollapsibleListData,
  Decoration,
  Decoration_Type,
  DividerData,
  EmbedData,
  FileData,
  GalleryData,
  GIFData,
  HeadingData,
  HTMLData,
  ImageData,
  Link,
  LinkPreviewData,
  Metadata,
  Node,
  NodeStyle,
  OrderedListData,
  ParagraphData,
  PollData,
  TableCellData,
  TableData,
  TextData,
  VideoData,
} from './../../ricos-schema/legacy/wix/rich_content/v1';
import { LATEST_VERSION, Link_Target, Node_Type, TextStyle_TextAlignment } from './../../ricos-schema/legacy/wix/rich_content/v1';

import type { RicosEntityRange, RicosInlineStyleRange } from '../types';
import { fromEntries } from '../utils';
import { generateId } from './generateRandomId';

export const createNode = <TData>(
  type: Node_Type,
  {
    nodes,
    data,
    style,
    id,
  }: {
    nodes: Node[];
    data: TData;
    style?: NodeStyle;
    id?: string;
  }
): Node => ({
  type,
  id: id ?? generateId(),
  nodes,
  ...dataByNodeType(type, data),
  style,
});

export const dataByNodeType = (type: Node_Type, data: unknown) => {
  const map = {
    [Node_Type.CODE_BLOCK]: { codeBlockData: data as CodeBlockData },
    [Node_Type.DIVIDER]: { dividerData: data as DividerData },
    [Node_Type.HEADING]: { headingData: data as HeadingData },
    [Node_Type.FILE]: { fileData: data as FileData },
    [Node_Type.GALLERY]: { galleryData: data as GalleryData },
    [Node_Type.GIF]: { gifData: data as GIFData },
    [Node_Type.HTML]: { htmlData: data as HTMLData },
    [Node_Type.IMAGE]: { imageData: data as ImageData },
    [Node_Type.BUTTON]: { buttonData: data as ButtonData },
    [Node_Type.PARAGRAPH]: { paragraphData: data as ParagraphData },
    [Node_Type.POLL]: { pollData: data as PollData },
    [Node_Type.TEXT]: { textData: data as TextData },
    [Node_Type.VIDEO]: { videoData: data as VideoData },
    [Node_Type.AUDIO]: { audioData: data as AudioData },
    [Node_Type.EMBED]: { embedData: data as EmbedData },
    [Node_Type.APP_EMBED]: { appEmbedData: data as AppEmbedData },
    [Node_Type.LINK_PREVIEW]: { linkPreviewData: data as LinkPreviewData },
    [Node_Type.TABLE]: { tableData: data as TableData },
    [Node_Type.TABLE_CELL]: { tableCellData: data as TableCellData },
    [Node_Type.COLLAPSIBLE_LIST]: { collapsibleListData: data as CollapsibleListData },
    [Node_Type.BULLETED_LIST]: {
      bulletedListData: data as BulletedListData,
    },
    [Node_Type.ORDERED_LIST]: {
      orderedListData: data as OrderedListData,
    },
  };

  return map[type as $TSFixMeKey<typeof map>];
};

export const createParagraphNode = (
  nodes: Node[] = [],
  data?: ParagraphData,
  style?: NodeStyle
): Node =>
  createNode(Node_Type.PARAGRAPH, {
    nodes,
    data: {
      textStyle: { textAlignment: TextStyle_TextAlignment.AUTO },
      ...data,
    },
    style,
  });

export const createTextNode = (text: string, decorations: Decoration[] = []): Node =>
  createNode(Node_Type.TEXT, { nodes: [], id: '', data: { text, decorations } });

export const createHeadingNode = (nodes: Node[] = [], data: HeadingData): Node =>
  createNode(Node_Type.HEADING, {
    nodes,
    data: {
      textStyle: { textAlignment: TextStyle_TextAlignment.AUTO },
      ...data,
    },
  });

export const createDecoration = (
  type: Decoration_Type,
  data: Omit<Decoration, 'type'> = {}
): Decoration => ({ type, ...data });

export const initializeMetadata = ({
  version,
  id = uuid(),
  createdTimestamp,
  updatedTimestamp,
}: {
  version?: number;
  id?: string;
  createdTimestamp?: Date;
  updatedTimestamp?: Date;
} = {}): Metadata => ({
  version: version || LATEST_VERSION,
  createdTimestamp: createdTimestamp || new Date(),
  updatedTimestamp: updatedTimestamp || new Date(),
  id,
});

type DecorationMap = Record<Decoration_Type, Decoration>;

export const reduceDecorations = (decorations: Decoration[]): Decoration[] => {
  const reducedDecorationsMap: DecorationMap = decorations.reduce(
    (decorationMap, { type, ...data }) => {
      const currentDecoration: Decoration = decorationMap[type] || { type };
      const nextDecoration: Decoration = { type, ...data };
      return {
        ...decorationMap,
        [type]: lodash.merge(currentDecoration, nextDecoration),
      };
    },
    {} as DecorationMap
  );
  const reducedDecorations = Object.values(reducedDecorationsMap);
  return reducedDecorations;
};

const toLinkTarget = (target = 'BLANK') => {
  switch (pipe(target, S.toUpperCase, S.replace('_', ''))) {
    case 'SELF': {
      return Link_Target.SELF;
    }
    case 'BLANK': {
      return Link_Target.BLANK;
    }
    case 'PARENT': {
      return Link_Target.PARENT;
    }
    case 'TOP': {
      return Link_Target.TOP;
    }
    default: {
      return Link_Target.BLANK;
    }
  }
};

export const createLink = ({
  url,
  rel,
  target,
  anchor,
}: {
  url?: string;
  rel?: string;
  target?: string;
  anchor?: string;
}): Link => {
  const relValues: [string, boolean][] =
    (typeof rel === 'string' ? rel : '')
      .split(' ')
      .filter(key => ['nofollow', 'sponsored', 'ugc', 'noreferrer'].includes(key))
      .map(key => [key, true]) || [];

  return {
    anchor,
    url,
    rel: relValues.length > 0 ? fromEntries(relValues) : undefined,
    target: toLinkTarget(typeof target === 'string' ? target : undefined),
  };
};

export const parseLink = ({
  url,
  rel,
  target,
  anchor,
  customData,
}: Link): {
  url?: string;
  rel?: string;
  target?: string;
  anchor?: string;
  customData?: string;
} => ({
  anchor,
  url,
  rel:
    rel &&
    Object.entries(rel)
      .flatMap(([key, value]) => (value ? key : []))
      .join(' '),
  target: target && '_' + target.toLowerCase(),
  customData,
});

const last = (arr: $TSFixMe) => (arr.length > 0 ? arr[arr.length - 1] : null);

export const partitionBy =
  <T>(
    isSeparator: (node: T) => boolean,
    isPartition: (node: T) => boolean,
    Separator: (node: T) => T,
    Partition: (node: T) => T,
    addToPartition: (partition: T, node: T) => void
  ) =>
  (nodes: T[]): T[] =>
    nodes.reduce((partitions: T[], node: T) => {
      if (isSeparator(node)) {
        partitions.push(Separator(node));
      } else {
        let lastPartition = last(partitions);
        if (!lastPartition || !isPartition(lastPartition)) {
          const partition = Partition(node);
          partitions.push(partition);
          lastPartition = last(partitions);
        }
        addToPartition(lastPartition, node);
      }
      return partitions;
    }, []);

type RicosInlineStyleRangeWithStyle = RicosInlineStyleRange & {
  styleKey: string;
  styleValue: string;
};

const getRangeStyle = (range: RicosInlineStyleRange) => {
  try {
    const style = JSON.parse(range.style);
    return { styleKey: Object.keys(style)[0], styleValue: Object.values(style)[0] };
  } catch (e) {
    return { styleKey: range.style, styleValue: range.style };
  }
};

export const removeOverlappingRangesWithSameStyle = (ranges: RicosInlineStyleRange[]) => {
  const isOverlapping = (a: RicosInlineStyleRangeWithStyle, b: RicosInlineStyleRangeWithStyle) =>
    a.styleKey === b.styleKey &&
    a.styleValue === b.styleValue &&
    a.offset <= b.offset + b.length &&
    a.offset + a.length >= b.offset;

  return (
    ranges
      .map(
        // @ts-ignore $TSFixMe
        (range): RicosInlineStyleRangeWithStyle => ({
          ...range,
          ...getRangeStyle(range),
        })
      )
      .reduce<RicosInlineStyleRangeWithStyle[]>((acc, range) => {
        const filteredRanges = acc.filter(r => {
          return !(
            range.styleKey === r.styleKey &&
            r.offset >= range.offset &&
            r.offset + r.length <= range.offset + range.length
          );
        });

        return mergeRangeWithList(filteredRanges, range, isOverlapping);
      }, [])
      .reduce<RicosInlineStyleRangeWithStyle[]>((acc, range) => {
        return adjustAndAddRange(acc, range);
      }, [])
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .map(({ styleKey, styleValue, ...range }) => range)
  );
};

const adjustAndAddRange = (
  existingRanges: RicosInlineStyleRangeWithStyle[],
  newRange: RicosInlineStyleRangeWithStyle
): RicosInlineStyleRangeWithStyle[] => {
  const isOverlapping = (a: RicosInlineStyleRangeWithStyle, b: RicosInlineStyleRangeWithStyle) =>
    (a.styleKey === b.styleKey ||
      `NOT_${a.styleKey}` === b.styleKey ||
      a.styleKey === `NOT_${b.styleKey}`) &&
    a.offset <= b.offset + b.length &&
    a.offset + a.length >= b.offset;

  const adjust = (
    existing: RicosInlineStyleRangeWithStyle,
    newR: RicosInlineStyleRangeWithStyle
  ): RicosInlineStyleRangeWithStyle => {
    if (isOverlapping(existing, newR)) {
      if (existing.offset === newR.offset) {
        return {
          ...existing,
          offset: newR.offset + newR.length,
          length: existing.length - newR.length,
        };
      } else if (existing.offset > newR.offset) {
        const overlapLength = newR.offset + newR.length - existing.offset;
        return {
          ...existing,
          offset: existing.offset + overlapLength,
          length: existing.length - overlapLength,
        };
      } else {
        return { ...existing, length: newR.offset - existing.offset };
      }
    }
    return existing;
  };

  const adjustedRanges = existingRanges
    .map(range => adjust(range, newRange))
    .filter(range => range.length > 0);

  adjustedRanges.push(newRange);

  return adjustedRanges;
};

export const mergeOverlappingEntityRanges = (
  entityRanges: RicosEntityRange[]
): RicosEntityRange[] => {
  const isOverlapping = (a: RicosEntityRange, b: RicosEntityRange) =>
    a.key === b.key && a.offset <= b.offset + b.length && a.offset + a.length >= b.offset;

  return entityRanges.reduce(
    (acc: RicosEntityRange[], range: RicosEntityRange) =>
      mergeRangeWithList(acc, range, isOverlapping),
    [] as RicosEntityRange[]
  );
};

const mergeRangeWithList = <T extends { offset: number; length: number }>(
  ranges: T[],
  newRange: T,
  isOverlapping: (a: T, b: T) => boolean
): T[] => {
  const mergeRanges = (a: T, b: T) => ({
    ...a,
    offset: Math.min(a.offset, b.offset),
    length: Math.max(a.offset + a.length, b.offset + b.length) - Math.min(a.offset, b.offset),
  });

  const result = ranges.reduce(
    (acc: { ranges: T[]; currentMerging: T }, currentRange) => {
      if (isOverlapping(acc.currentMerging, currentRange)) {
        acc.currentMerging = mergeRanges(acc.currentMerging, currentRange);
        return acc;
      }

      acc.ranges.push(currentRange);

      return acc;
    },
    { ranges: [], currentMerging: newRange }
  );

  result.ranges.push(result.currentMerging);

  return result.ranges;
};

export const generateRangeMap = (ranges: (RicosInlineStyleRange | RicosEntityRange)[]) => {
  const rangeMap: $TSFixMe = {};
  ranges.forEach(({ offset, length, ...props }) => {
    rangeMap[offset] = [...(rangeMap[offset] || []), { action: 'start', ...props, length }];
    rangeMap[offset + length] = [
      ...(rangeMap[offset + length] || []),
      { action: 'end', ...props, length },
    ];
  });

  const normalizedRangeMap = normalizeRangeMap(rangeMap);

  if (!normalizedRangeMap[0]) {
    normalizedRangeMap[0] = [];
  }

  return normalizedRangeMap;
};

type RangeMapEntry = (
  | Omit<RicosInlineStyleRange, 'offset' | 'length'>
  | Omit<RicosEntityRange, 'offset' | 'length'>
) & {
  action: 'start' | 'end';
};

type RangeMapEntryWithLength = RangeMapEntry & { length: number };

const getRangeEntityId = (entry: RangeMapEntry) => {
  if ('key' in entry) {
    return entry.key;
  }

  return entry.style;
};

const isEntryOverlapping = (current: RangeMapEntryWithLength, next: RangeMapEntryWithLength) => {
  return (
    getRangeEntityId(current) === getRangeEntityId(next) &&
    current.action === 'end' &&
    next.action === 'start' &&
    // Ranges of length 1 are not overlapping
    current.length !== 1 &&
    next.length !== 1
  );
};

const getStyleKeyVariation = (entry: RangeMapEntry) => {
  if ('key' in entry) {
    return entry.key;
  }

  try {
    const style = JSON.parse(entry.style);
    return Object.keys(style)[0];
  } catch {
    return entry.style;
  }
};

const getStyleKeyValue = (entry: RangeMapEntry) => {
  if ('key' in entry) {
    return entry.key;
  }

  return entry.style;
};

const mergeAdjacentRanges = (
  rangeMap: Record<number, RangeMapEntryWithLength[]>
): Record<number, RangeMapEntry[]> => {
  const newRangeMap = lodash.cloneDeep(rangeMap);

  Object.keys(newRangeMap).forEach(key => {
    const intKey = parseInt(key);
    if (!newRangeMap[intKey + 1]) {
      return;
    }
    // Merge only if no other style is ending/starting at the same time
    const hasMultipleSameStyleEntries = newRangeMap[intKey].some(current => {
      return newRangeMap[intKey].some(next =>
        current === next ? false : getStyleKeyVariation(current) === getStyleKeyVariation(next)
      );
    });

    if (hasMultipleSameStyleEntries) {
      return;
    }

    const currentRangeBeforeUpdate = newRangeMap[intKey];
    // filter out ranges that are overlapping with the next range in both entries
    newRangeMap[intKey] = newRangeMap[intKey].filter(current => {
      return newRangeMap[intKey + 1].every(next => !isEntryOverlapping(current, next));
    });
    newRangeMap[intKey + 1] = newRangeMap[intKey + 1].filter(next => {
      return currentRangeBeforeUpdate.every(current => !isEntryOverlapping(current, next));
    });
  });

  return Object.entries(newRangeMap).reduce((acc, [key, value]) => {
    return {
      ...acc,
      [key]: value.map(v => ({ ...v, length: undefined })),
    };
  }, {} as Record<number, RangeMapEntry[]>);
};

const moveEndOfRanges = (rangeMap: Record<number, RangeMapEntry[]>) => {
  const newRangeMap = lodash.cloneDeep(rangeMap);
  Object.keys(newRangeMap)
    .reverse()
    .forEach((k, index, allKeys) => {
      const key = parseInt(k);
      // eslint-disable-next-line fp/no-loops
      for (const current of newRangeMap[key]) {
        if (current.action !== 'end') {
          continue;
        }
        const currentStyleKey = getStyleKeyVariation(current);
        let isMovable = false;
        let hasValidStart = false;

        const sameStyleStartInSameKey = newRangeMap[key].find((entry: $TSFixMe) => {
          return getStyleKeyValue(entry) === getStyleKeyValue(current) && entry.action === 'start';
        });

        if (sameStyleStartInSameKey) {
          newRangeMap[key] = newRangeMap[key].filter(
            (entry: $TSFixMe) => ![current, sameStyleStartInSameKey].includes(entry)
          );
          continue;
        }

        // eslint-disable-next-line fp/no-loops
        for (let i = index + 1; i < allKeys.length; i++) {
          const prevKey = Number(allKeys[i]);

          // if first encounter of same style is a start,
          // it's a valid case, continue to the next one
          hasValidStart = newRangeMap[prevKey].some((prev: $TSFixMe) => {
            return prev.action === 'start' && getRangeEntityId(current) === getRangeEntityId(prev);
          });

          if (hasValidStart) {
            break;
          }

          isMovable = newRangeMap[prevKey].some((prev: $TSFixMe) => {
            const prevStyleKey = getStyleKeyVariation(prev);
            return (
              prev.action === 'start' &&
              currentStyleKey === prevStyleKey &&
              getRangeEntityId(current) !== getRangeEntityId(prev)
            );
          });

          if (isMovable) {
            newRangeMap[prevKey].push(current);
            break;
          }
        }

        if (hasValidStart) {
          continue;
        }

        if (isMovable) {
          newRangeMap[key] = newRangeMap[key].filter((entry: $TSFixMe) => entry !== current);
        }
      }
    });

  return newRangeMap;
};

export const normalizeRangeMap = (rangeMap: Record<number, RangeMapEntryWithLength[]>) => {
  const newRangeMap = moveEndOfRanges(mergeAdjacentRanges(rangeMap));

  // remove empty ranges
  Object.keys(newRangeMap).forEach(key => {
    if (newRangeMap[Number(key)]?.length === 0) {
      // eslint-disable-next-line fp/no-delete
      delete newRangeMap[Number(key)];
    }
  });

  return newRangeMap;
};
